var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Styled from "./box-parameters.style";
import { CanvasOverlay } from "./canvas-overlay";
import { ParameterList } from "ui/components/elements/custom-drawer";
import { ObjectParameterMenu } from "ui/components/panels/sidebar/tree-nav-menus/object-parameter-menu";
import { useCloseOnOutsidePointer } from "hooks/use-close-on-outside-pointer";
/* The Tacton parameter group holding the object's weight and other properties —
 * the same group the sidebar's object menu opens. */
var objectParameterGroupId = "object_group";
/**
 * The parameters of the cardboard box that was selected in the scene: its own
 * (`children`), then the object's — weight (from the Tacton object group) and
 * size/shape.
 *
 * The object's parameters are shared by every box on the pallet, not this one
 * box's, and the panel says so. They are the same store fields the sidebar's
 * object menu edits; both are views of one value, not two sources of truth.
 *
 * Closes on its own control or on a pointer outside it. Note what is NOT used
 * here: `useBlockingElementEffect` detaches the mouse from the whole PlayCanvas
 * scene while the panel is mounted, which left no way to deselect the box the
 * panel belongs to — the panel could not be closed at all.
 *
 * Anything else that is part of the selected box's UI has to be passed in as
 * `insideRefs`. The close hook listens on `document` in the capture phase, so
 * it runs before React delivers a click to a button in a sibling overlay: left
 * out, such a button would deselect the box and unmount itself before its own
 * handler ever ran (which is exactly what made the box edit controls dead).
 */
export var BoxParameters = function (_a) {
    var centerX = _a.centerX, centerY = _a.centerY, title = _a.title, onClose = _a.onClose, insideRefs = _a.insideRefs, children = _a.children;
    var t = useTranslation().t;
    var panelRef = useRef(null);
    useCloseOnOutsidePointer(true, __spreadArray([panelRef], __read((insideRefs !== null && insideRefs !== void 0 ? insideRefs : [])), false), onClose);
    return (_jsx(CanvasOverlay, { x: centerX, y: centerY, children: _jsxs(Styled.BoxParameters, { ref: panelRef, 
            /* The canvas picker listens for mouse-down; without this, using the
               panel would also deselect the box or start dragging the scene. */
            onMouseDown: function (event) { return event.stopPropagation(); }, onWheel: function (event) { return event.stopPropagation(); }, children: [_jsx("div", { className: "".concat(Styled.prefix, "-header"), children: _jsxs("div", { className: "".concat(Styled.prefix, "-title-row"), children: [_jsx("span", { className: "".concat(Styled.prefix, "-title"), children: title }), _jsx("button", { className: "".concat(Styled.prefix, "-close"), onClick: onClose, "aria-label": t("cardboard_box.close"), children: "\u00D7" })] }) }), children, _jsx("span", { className: "".concat(Styled.prefix, "-shared-note"), children: t("cardboard_box.shared_parameters_note") }), _jsx(ParameterList, { groupIds: [objectParameterGroupId] }), _jsx(ObjectParameterMenu, { showPallet: false })] }) }));
};
